import React, { useState } from 'react';
import {
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import { Menu as MenuIcon, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom-v5-compat';
import { slugify } from 'common';
import type { MenuItem as BaseMenuItem } from '../../hooks/useMenuItems';

type MenuItem = BaseMenuItem & {
  label?: string;
  Icon?: React.ElementType;
  hasChildren?: boolean;
  onClick?: (evt: React.MouseEvent, item: MenuItem) => void;
};

type HeaderMobileMenuProps = {
  items?: MenuItem[];
  id?: string;
};

type MenuItemWithMeta = MenuItem & {
  menuItemId: string;
  hasChildren: boolean;
  isExpanded: boolean;
};

export default function HeaderMobileMenu({
  items,
  id = 'mobile-menu',
}: HeaderMobileMenuProps): React.ReactElement {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const toggleDrawer =
    (isDrawerOpen: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent): void => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }

      setIsOpen(isDrawerOpen);
    };

  const handleSubmenuClick = (
    evt: React.MouseEvent,
    { onClick, ...item }: MenuItemWithMeta
  ): void => {
    const { hasChildren, menuItemId, isExpanded, path } = item;

    if (hasChildren) {
      setActiveSubmenu(isExpanded ? null : menuItemId);
    }

    if (path) {
      navigate({ pathname: path });
      setIsOpen(false);
    }
    onClick?.(evt, item);
  };

  const getLists = (menuItems: MenuItem[], depth = 0): React.ReactElement => {
    return (
      <List
        sx={{
          width: depth ? '100%' : 195,
          bgcolor: depth % 2 === 0 ? 'background.paper' : 'grey.100',
          color: depth % 2 === 0 ? 'primary.main' : 'text.primary',
        }}
        component="nav"
        className="flex flex-col flex-1"
      >
        {menuItems.map((item) => {
          const {
            title,
            label = title,
            children,
            Icon,
            hasChildren = !!children?.length,
          } = item;
          const menuItemId = `${id}-${slugify(String(label ?? ''))}-${depth}`;
          const isExpanded = activeSubmenu === menuItemId;

          return (
            <div key={menuItemId}>
              <ListItemButton
                id={menuItemId}
                onClick={(evt) =>
                  handleSubmenuClick(evt, {
                    ...item,
                    menuItemId,
                    hasChildren,
                    isExpanded,
                  })
                }
              >
                {Icon && (
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                )}
                <ListItemText primary={label} />
                {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
              {hasChildren && children && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  {getLists(children as MenuItem[], depth + 1)}
                </Collapse>
              )}
            </div>
          );
        })}
      </List>
    );
  };

  return (
    <>
      <IconButton
        edge="start"
        color="inherit"
        aria-label="menu"
        size="large"
        onClick={toggleDrawer(true)}
        id={id}
      >
        <MenuIcon data-cy="MenuIcon" />
      </IconButton>
      <Drawer anchor="left" open={isOpen} onClose={toggleDrawer(false)}>
        {items && getLists(items)}
      </Drawer>
    </>
  );
}
